import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, HeartHandshake, BookOpen, Users, Sparkles, MessageCircle, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";

const HERO = "/hero-brasileiro.jpg";
const COMMUNITY = "https://plus.unsplash.com/premium_photo-1682094149359-c7acbcfa97ca?fm=jpg&q=85&w=2000&auto=format&fit=crop&ixlib=rb-4.1.0";
const TIMELINE_IMG = "https://images.unsplash.com/photo-1516890896652-41ca1a35787c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxjaGlsZHJlbiUyMHBsYXlpbmclMjBvdXRkb29ycyUyMHNvZnQlMjBsaWdodHxlbnwwfHx8fDE3ODM5NTg2MDd8MA&ixlib=rb-4.1.0&q=85";

const PILLARS = [
  { icon: BookOpen, title: "Informação de qualidade", text: "Conteúdos práticos revisados por profissionais para orientar o dia a dia da família." },
  { icon: Users, title: "Comunidade acolhedora", text: "Um espaço para trocar experiências com outras famílias no mesmo caminho." },
  { icon: HeartHandshake, title: "Pré-atendimento cuidadoso", text: "Nosso time responde pelo WhatsApp para entender sua necessidade e direcionar." },
  { icon: ShieldCheck, title: "Ambiente seguro", text: "Cadastro protegido, dados sigilosos e comunicação sem julgamentos." },
];

export default function Home() {
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/posts", { params: { limit: 3 } })
      .then(({ data }) => setPosts(data))
      .catch(() => {});
    api.get("/stats/public")
      .then(({ data }) => setStats(data))
      .catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[hsl(var(--muted))] to-transparent" />
        <div className="container-cuidar pt-16 pb-20 grid gap-14 lg:grid-cols-[1.15fr_1fr] items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[hsl(var(--border))] px-3 py-1.5 text-xs font-medium text-[hsl(var(--muted-foreground))]">
              <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--secondary))]" />
              Um lugar para famílias neuroatípicas
            </div>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight text-[hsl(var(--foreground))]">
              Cuidar é caminhar <span className="text-[hsl(var(--primary))]">ao lado</span>. Todo dia, no ritmo da sua família.
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed">
              O Instituto Cuidar acolhe famílias de crianças com TEA, TDAH, dislexia e outras neurodivergências, com informação, comunidade e pré-atendimento humano — inclusive pelo WhatsApp.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="btn-pill bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 px-7 h-12">
                <Link to="/cadastro" data-testid="hero-cta-cadastro">Criar conta gratuita <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="btn-pill h-12 px-7">
                <Link to="/comunidade" data-testid="hero-cta-comunidade">
                  <Users className="mr-2 h-4 w-4" /> Entrar na comunidade
                </Link>
              </Button>
            </div>
            <div className="mt-10">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--secondary))] mb-3 inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--primary))] animate-pulse" />
                Transparência em tempo real
              </div>
              <div className="grid grid-cols-3 gap-6 max-w-md" data-testid="home-stats">
                <Stat n={stats?.families} l="famílias acolhidas" testId="stat-families" />
                <Stat n={stats?.courses} l="cursos gratuitos" testId="stat-courses" />
                <Stat n={stats?.whatsapp_conversations} l="atendimentos no WhatsApp" testId="stat-whatsapp" />
              </div>
            </div>
          </div>

          <div className="relative fade-up fade-up-2">
            <div className="absolute -inset-4 rounded-[2rem] bg-[hsl(var(--secondary))]/25 -z-10" />
            <img
              src={HERO}
              alt="Adulto e criança em luz natural"
              className="w-full h-[420px] sm:h-[500px] object-cover rounded-[2rem] shadow-[0_20px_60px_rgba(0,0,0,0.12)]"
            />
            <div className="absolute -bottom-6 -left-4 sm:-left-6 max-w-[260px] surface p-4 flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-full bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))]">
                <HeartHandshake className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm font-heading font-bold">Pré-atendimento humano</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">Fale pelo WhatsApp sempre que precisar</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="container-cuidar py-12">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {PILLARS.map((p, i) => (
            <Card key={p.title} data-testid={`pillar-${i}`} className={`surface hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-300`}>
              <CardContent className="p-6">
                <div className="grid h-11 w-11 place-items-center rounded-2xl bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
                  <p.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-heading text-lg font-bold">{p.title}</h3>
                <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{p.text}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Community band */}
      <section className="container-cuidar py-16">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr] items-center">
          <div className="relative">
            <img src={COMMUNITY} alt="Comunidade acolhedora" className="w-full h-[380px] object-cover rounded-[2rem]" />
            <div className="absolute inset-0 rounded-[2rem] ring-1 ring-black/5" />
          </div>
          <div>
            <div className="text-xs font-semibold tracking-widest uppercase text-[hsl(var(--secondary))]">Nossa missão</div>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold leading-tight">
              Cada criança é única. Cada família também.
            </h2>
            <p className="mt-4 text-[hsl(var(--muted-foreground))] leading-relaxed">
              Trabalhamos para que nenhuma família se sinta sozinha na jornada da neurodivergência.
              Aqui, você encontra escuta ativa, materiais confiáveis e pessoas que entendem, sem julgamento.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {[
                "Orientação inicial gratuita pelo WhatsApp",
                "Conteúdos revisados por psicólogos, fonos e pedagogos",
                "Encontros online e presenciais com outras famílias",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-1 inline-block h-2 w-2 rounded-full bg-[hsl(var(--primary))]" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex gap-3">
              <Button asChild className="btn-pill bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">
                <Link to="/sobre" data-testid="home-cta-sobre">Sobre o instituto</Link>
              </Button>
              <Button asChild variant="outline" className="btn-pill">
                <Link to="/timeline" data-testid="home-cta-timeline">Ver postagens</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Latest posts */}
      <section className="container-cuidar py-16">
        <div className="flex items-end justify-between gap-6 mb-8">
          <div>
            <div className="text-xs font-semibold tracking-widest uppercase text-[hsl(var(--accent))]">Novidades</div>
            <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-extrabold">Do nosso timeline</h2>
          </div>
          <Link to="/timeline" className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-[hsl(var(--primary))] hover:underline" data-testid="home-timeline-more">
            Ver tudo <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {(posts.length ? posts : [null, null, null]).map((p, i) => (
            <PostCardMini key={p?.id || i} post={p} fallback={TIMELINE_IMG} />
          ))}
        </div>
      </section>
    </div>
  );
}

function Stat({ n, l, testId }) {
  const display = n === undefined || n === null
    ? <span className="inline-block h-6 w-14 bg-[hsl(var(--muted))] rounded animate-pulse align-middle" />
    : new Intl.NumberFormat("pt-BR").format(n);
  return (
    <div data-testid={testId}>
      <div className="font-heading text-2xl font-extrabold text-[hsl(var(--primary))]">{display}</div>
      <div className="text-xs text-[hsl(var(--muted-foreground))]">{l}</div>
    </div>
  );
}

function PostCardMini({ post, fallback }) {
  if (!post) {
    return <div className="surface h-[300px] animate-pulse bg-[hsl(var(--muted))]" />;
  }
  return (
    <Link to={`/postagem/${post.id}`} data-testid={`home-post-card-${post.id}`} className="group surface overflow-hidden block hover:-translate-y-0.5 transition-transform duration-300">
      <div className="h-44 overflow-hidden">
        <img src={post.cover_image || fallback} alt={post.title} className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
      </div>
      <div className="p-5">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-[hsl(var(--secondary))]">{post.category || "Postagem"}</div>
        <h3 className="mt-2 font-heading text-lg font-bold leading-snug line-clamp-2">{post.title}</h3>
        <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] line-clamp-2">{post.excerpt}</p>
      </div>
    </Link>
  );
}
